import { useState } from "react";
import { AnswerOption } from "../../components/AnswerOption";
import { Button } from "../../components/Button";
import type { PracticeMode, Question } from "../../lib/practice";
import { QuestionText } from "../../components/QuestionText";

interface QuestionScreenProps {
  question: Question;
  mode: PracticeMode;
  /** Trial mode only — questions left before the upgrade wall. */
  trialRemaining?: number | undefined;
  /** `chosenOptionId` is what the server marks; the boolean is for the UI. */
  onNext: (wasCorrect: boolean, chosenOptionId: string | null) => void;
  onAskTutor?: (() => void) | undefined;
  /**
   * Leave the session.
   *
   * Screen 9's spec puts a close control in the header. It was never built, and
   * the tab bar is deliberately hidden mid-session, so a student who opened a
   * session had no way out of it at all except the browser's back button.
   */
  onClose?: (() => void) | undefined;
}

const MODE_LABEL: Record<PracticeMode, string> = {
  "past-question": "Past Questions",
  topic: "Topic Practice",
  trial: "Free preview",
  offline: "Past Questions",
};

/**
 * Screen 9 — one component, four modes via props.
 *
 * The explanation renders whether the answer was right or wrong. A student who
 * guessed correctly learns nothing from "Correct" alone, and the mastery model
 * treats a lucky guess and a known answer differently anyway.
 *
 * Offline hides the tutor and says why. It does not hide practice: screens 9,
 * 10, 15, 16 and 19 must stay usable without data, and a wall here would strand
 * a student mid-session on a train.
 */
export function QuestionScreen({
  question,
  mode,
  trialRemaining,
  onNext,
  onAskTutor,
  onClose,
}: QuestionScreenProps) {
  const [chosen, setChosen] = useState<string | null>(null);

  /*
   * A new question is a clean slate.
   *
   * The route advances by changing the `question` prop on the same component,
   * so React kept the previous answer: question 2 opened already showing
   * "Correct"/"Not quite" and the coloured options from question 1, with the
   * real options locked. This is React's documented way to reset state when a
   * prop changes — it re-renders immediately, before anything paints, rather
   * than flashing the stale state through an effect.
   */
  const [shownQuestionId, setShownQuestionId] = useState(question.id);
  if (shownQuestionId !== question.id) {
    setShownQuestionId(question.id);
    setChosen(null);
  }

  const answered = chosen !== null;
  const wasCorrect = chosen === question.correctOptionId;

  const stateFor = (optionId: string) => {
    if (!answered) return chosen === optionId ? "selected" : "idle";
    if (optionId === question.correctOptionId) return "correct";
    if (optionId === chosen) return "wrong";
    return "idle";
  };

  return (
    <div className="app-column px-5 pb-6 pt-4">
      {mode === "offline" ? (
        <div
          role="status"
          className="mb-3 rounded-[var(--radius-control)] border border-border bg-surface px-3 py-2 text-[12px] text-text-muted"
        >
          Offline — your practice is saving and will sync when you're back.
        </div>
      ) : null}

      <header className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Leave this session"
              className="-ml-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-control)] text-[18px] leading-none text-text-muted"
            >
              ✕
            </button>
          ) : null}
          <p className="m-0 truncate text-[12px] text-text-muted">
            {question.subject} · {MODE_LABEL[mode]} · {question.source}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/*
            Shown whenever the server sends a countdown, not only in "trial"
            mode. A student on the free allowance practising past questions is
            still doing past questions — relabelling the whole screen "Free
            preview" to make the chip appear hid what they were working on.
          */}
          {trialRemaining !== undefined ? (
            <span className="tabular rounded-[var(--radius-chip)] border border-accent bg-accent-tint px-2 py-1 text-[11px] font-semibold">
              {trialRemaining} free left
            </span>
          ) : null}
          <span className="tabular text-[12px] text-text-muted">
            Q{question.index} of {question.total}
          </span>
        </div>
      </header>

      <QuestionText as="h1" className="m-0 pt-5 text-[17px] font-semibold leading-[1.45]">
        {question.stem}
      </QuestionText>

      <ul className="m-0 flex list-none flex-col gap-2.5 p-0 pt-5">
        {question.options.map((option, index) => (
          <li key={option.id}>
            <AnswerOption
              letter={String.fromCharCode(65 + index)}
              text={option.text}
              state={stateFor(option.id)}
              disabled={answered}
              onClick={answered ? undefined : () => setChosen(option.id)}
            />
          </li>
        ))}
      </ul>

      {answered ? (
        <section className="mt-5 flex flex-col gap-3 rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <p
            className={`m-0 text-[13px] font-bold ${wasCorrect ? "text-positive" : "text-negative"}`}
          >
            {wasCorrect ? "Correct" : "Not quite"}
          </p>
          <QuestionText as="p" className="m-0 text-[13.5px] leading-[1.6] text-text-muted">
            {question.explanation}
          </QuestionText>

          {mode === "offline" ? (
            // Full width, then the note on its own line: side by side, the note
            // was squeezed into a one-word column beside a giant button.
            <div className="flex flex-col gap-2 pt-1">
              <Button className="text-[13px]" onClick={() => onNext(wasCorrect, chosen)}>
                Next question
              </Button>
              <p className="m-0 text-center text-[12px] leading-[1.45] text-text-muted">
                The tutor needs internet. Your practice doesn't.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Button variant="secondary" className="text-[13px]" onClick={onAskTutor}>
                Ask the tutor
              </Button>
              <Button className="text-[13px]" onClick={() => onNext(wasCorrect, chosen)}>
                Next question
              </Button>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
